import * as classNames from 'classnames';
import { ajax } from 'jquery';
import { compact } from 'lodash';
import { contentType } from 'mime-types';
import * as React from 'react';
import ReactDropzone from 'react-dropzone';
import { ajaxErrorSwal } from '../../TsUtils';
import { directUpload } from '../../utilities/directUpload';
import { numberToHumanSize } from '../../utilities/numberToHumanSize';
import { toSentence } from '../../utilities/toSentence';

export interface IDropzone {
  accept: string[];
  maxSize?: number;
  multiple: boolean;
  disabled?: boolean;
  id?: string;
  name?: string;
  uploader: 'activestorage' | 'cloudinary';
  useFileUploadCallBack?: boolean;
  uploadCallback(file: File, signedId: string): void;
  setIsLoading?(values: boolean): void;
}

export function Dropzone({
  accept,
  maxSize,
  multiple,
  disabled,
  id,
  name,
  useFileUploadCallBack = false,
  setIsLoading,
  uploader,
  uploadCallback,
}: IDropzone) {
  const handleMultiDrop = React.useCallback((acceptedFiles: File[]) => {
    Array.from(acceptedFiles).map((file) => uploadFile(file));

    // LB NOTE: Using this to set the ValidatingSubmitButton to be disabled until the documents have uploaded
    if (setIsLoading) {
      setIsLoading(true);
    }
  }, []);

  const handleSingleDrop = (acceptedFiles: File[]) => {
    Array.from(acceptedFiles).forEach((file) => uploadFile(file));
  };

  // LB NOTE: We need to handle uploads slightly different for the bulk adding of candidates, so
  // evaluating which function to use here
  const handleDrop = useFileUploadCallBack ? handleMultiDrop : handleSingleDrop;

  const uploadFile = React.useCallback(
    (file) => {
      if (uploader === 'cloudinary') {
        const folderName = `uploads/${(window as any).railsEnv}`;

        const formData = new FormData();
        formData.append('file', file);
        formData.append('folder', folderName);
        formData.append('upload_preset', 'unsigned-uploads');
        formData.append('api_key', '526742523931488');

        ajax({
          contentType: false,
          data: formData,
          dataType: 'json',
          error: (jqXhr) => ajaxErrorSwal(jqXhr),
          processData: false,
          success: (response) => uploadCallback(file, response.public_id),
          type: 'POST',
          url: 'https://api.cloudinary.com/v1_1/pinpointhq/image/upload',
        });
      }

      if (uploader === 'activestorage') {
        directUpload(file)
          .then((result) => {
            uploadCallback(file, result.signed_id);
          })
          .catch(() => {
            // TODO: Handle the error
          });
      }
    },
    [handleDrop],
  );

  function dropzoneClass({ isDragAccept, isDragReject, rejectedFiles }) {
    return classNames(
      'dropzone',
      { 'dropzone--accepted': isDragAccept },
      { 'dropzone--rejected': isDragReject || rejectedFiles.length },
    );
  }

  function dropzoneText({ isDragAccept, isDragReject, rejectedFiles }) {
    let text: string;
    let icon: string;

    if (isDragReject) {
      icon = 'times';
      text = 'File is too large or the wrong type';
    } else if (isDragAccept) {
      icon = 'check';
      text = 'Drop it!';
    } else if (rejectedFiles.length) {
      icon = 'times';
      text = 'File is too large or the wrong type';
    } else {
      icon = 'file-image';
      text = "Drag 'n' drop or click to upload.";
    }
    return (
      <>
        <i className={`fal fa-${icon} mar-r-1`} />
        {text}
      </>
    );
  }

  // NOTE: (VR) For some reason, ReactDropZone doesn't seem to like the `contentType` returned for `.txt` files, which
  // is ['text/plain; charset=utf-8].
  // We have to manually convert it to 'text/plain' in order for it to work and allow .txt files to be uploaded.
  const acceptedFileTypes: string[] = compact(
    accept.map((fileType: string) => (fileType === 'txt' ? 'text/plain' : contentType(fileType))),
  );

  const limitFileType = acceptedFileTypes.length > 0;

  return (
    <>
      <ReactDropzone
        accept={limitFileType ? acceptedFileTypes : null}
        {...{ maxSize, multiple, disabled }}
        onDrop={handleDrop}
      >
        {(dropProps) => (
          <div
            {...dropProps.getRootProps({ className: dropzoneClass(dropProps) })}
            style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
          >
            <input {...dropProps.getInputProps()} name={name} id={id || null} />
            {dropzoneText(dropProps)}
          </div>
        )}
      </ReactDropzone>
      <div className="bp3-form-helper-text mar-t-1">
        <small>
          {limitFileType && <em>The following file types are accepted: {toSentence(accept)}.</em>}{' '}
          <MaxFileSize maxSize={maxSize} />
        </small>
      </div>
    </>
  );
}

function MaxFileSize({ maxSize }: { maxSize: number }) {
  if (!maxSize) return null;

  return <em>The max file size is {numberToHumanSize(maxSize)}.</em>;
}
